import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import '@/screens/Modals/ForgeResultModal.css'
import { UserCard } from 'common/schema/users/userCards'
import { Package, X } from 'lucide-react'

interface ForgeResultModalProps {
  isOpen: boolean
  onClose: () => void
  isSuccess: boolean
  resultCard?: UserCard | null
  tokensCost: number
  cardsConsumed: number
}

export const ForgeResultModal = ({
  isOpen,
  onClose,
  isSuccess,
  resultCard = null,
  tokensCost,
  cardsConsumed,
}: ForgeResultModalProps) => {
  const [showContent, setShowContent] = useState(false)

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setShowContent(true), 1000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="forge-modal-overlay" onClick={onClose}>
      <div className="forge-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="forge-modal-close" onClick={onClose}>
          <X size={24} />
        </button>

        <div className="forge-result-container">
          {!showContent ? (
            <div className="forge-animation">
              <div className="forge-circle"></div>
              <div className="forge-hammer"></div>
              <div className="forge-sparks">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="spark"></div>
                ))}
              </div>
            </div>
          ) : (
            <AnimatePresence>
              <motion.div
                className={`forge-result ${isSuccess ? 'success' : 'failure'}`}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', duration: 0.5 }}
              >
                <div className="forge-result-header">
                  {isSuccess ? (
                    <>
                      <h2>Forge Successful!</h2>
                      <p>You created a new card!</p>
                    </>
                  ) : (
                    <>
                      <h2>Forge Failed</h2>
                      <p>The cards were consumed in the process</p>
                    </>
                  )}
                </div>

                {isSuccess && resultCard && (
                  <motion.div
                    className="forge-result-card"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="forge-card-preview">
                      <img
                        src={resultCard.url || ''}
                        alt={resultCard.collection}
                      />
                    </div>
                    <div className="forge-card-details">
                      <h3>{resultCard.collection}</h3>
                      <span
                        className={`forge-badge forge-badge-${resultCard.rarity?.toLowerCase()}`}
                      >
                        {resultCard.rarity}
                      </span>
                    </div>
                  </motion.div>
                )}

                <motion.div
                  className="forge-result-stats"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="forge-stat">
                    <Package size={20} />
                    <span>{cardsConsumed} cards consumed</span>
                  </div>
                  <div className="forge-stat">
                    <span className="forge-token-icon">₮</span>
                    <span>{tokensCost} tokens spent</span>
                  </div>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  )
}
